/**
 * Üç yapılandırma listesinin ORTAK parçaları.
 *
 * Saha, pano ve tank listeleri farklı veriler gösterir ama aynı iskelete
 * oturur: sekmeler · arama · yükleniyor · hata · boş · tavan uyarısı. Bunlar
 * üç dosyada ayrı ayrı yazılsaydı üçü zamanla ayrışırdı — birinde "tekrar
 * dene" düğmesi olur, diğerinde olmazdı.
 */
import { Link, useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';

import { Button } from '../design/Button.tsx';
import { EmptyState, SkeletonList } from '../design/Feedback.tsx';
import { Field, Input } from '../design/Field.tsx';
import { Yetki } from '../auth/Yetki.tsx';
import { cx } from '../design/cx.ts';
import { izinVar, useSession } from '../auth/session.ts';
import { LISTE_TAVANI } from './api.ts';

/* ── Sekmeler ────────────────────────────────────────────────────────────── */

/**
 * Yapılandırmanın üç listesi arasında geçiş.
 *
 * Alt sekme çubuğuna EKLENMEDİLER: mobilde dört öğe zaten sınırda, altı
 * öğe dokunma hedeflerini 44 px'in altına indirirdi. Üçü tek bir "Sahalar"
 * gezinme öğesinin altında yaşıyor.
 *
 * Her sekme kendi izniyle korunuyor — `panel_view` olmayan kullanıcı sekmeyi
 * hiç görmez. ⚠️ Bu YALNIZ görünürlüktür; asıl kapı sunucudaki
 * `requirePermission` (bkz. `Yetki.tsx`).
 */
export function YapilandirmaSekmeleri(): React.JSX.Element {
  const yol = useRouterState({ select: (s) => s.location.pathname });

  return (
    /*
     * Dar ekranda yatay KAYAR (`overflow-x-auto`): beş sekme 320 px'e
     * sığmıyor ve sıkıştırmak dokunma hedeflerini 44 px'in altına indirirdi.
     */
    <nav aria-label="Bölümler" className="overflow-x-auto border-b border-border bg-surface px-2">
      <ul className="flex gap-1">
        {/* İZLEME — ana ekran. Aşağıdakiler YAPILANDIRMA. */}
        <Sekme to="/" etiket="Okumalar" aktif={yol === '/'} />
        <Sekme to="/sahalar" etiket="Sahalar" aktif={yol.startsWith('/sahalar')} />
        <Yetki gerekli="panel_view">
          <Sekme to="/panolar" etiket="Panolar" aktif={yol.startsWith('/panolar')} />
        </Yetki>
        <Yetki gerekli="view_tank">
          <Sekme to="/tanklar" etiket="Tanklar" aktif={yol.startsWith('/tanklar')} />
        </Yetki>
        {/*
         * Denetim `site_view` ile korunuyor — sunucudaki izinle aynı. Ayrı bir
         * izin uydurmadık: bulgular yalnız ad/sayı taşıyor, pano ayrıntısı yok.
         */}
        <Sekme to="/denetim" etiket="Denetim" aktif={yol.startsWith('/denetim')} />
        <Yetki gerekli="sharelink_view">
          <Sekme to="/paylasim" etiket="Paylaşım" aktif={yol.startsWith('/paylasim')} />
        </Yetki>
      </ul>
    </nav>
  );
}

function Sekme({
  to,
  etiket,
  aktif,
}: {
  readonly to: '/' | '/sahalar' | '/panolar' | '/tanklar' | '/denetim' | '/paylasim';
  readonly etiket: string;
  readonly aktif: boolean;
}): React.JSX.Element {
  return (
    <li>
      <Link
        to={to}
        aria-current={aktif ? 'page' : undefined}
        className={cx(
          'flex min-h-11 items-center border-b-2 px-3 text-sm font-medium',
          aktif
            ? 'border-brand-600 text-brand-700'
            : 'border-transparent text-ink-muted hover:text-ink',
        )}
      >
        {etiket}
      </Link>
    </li>
  );
}

/* ── Arama ───────────────────────────────────────────────────────────────── */

/**
 * Arama kutusu — değer ADRESTE tutulur, bileşende değil.
 *
 * Sebep: kullanıcı aramayı paylaşabilsin, geri tuşu çalışsın, ve sayfa
 * yenilendiğinde sonuç kaybolmasın. Ama her tuş vuruşunda adres değişirse
 * tarayıcı geçmişi yüzlerce girdiyle dolar ve geri tuşu kullanılamaz hâle
 * gelir — bu yüzden yazma DURDUKTAN 300 ms sonra yazılıyor (`replace`).
 */
export function AramaKutusu({
  deger,
  degisti,
  ipucu,
}: {
  readonly deger: string;
  readonly degisti: (v: string) => void;
  readonly ipucu: string;
}): React.JSX.Element {
  const [yerel, setYerel] = useState(deger);

  // Adres DIŞARIDAN değişirse (geri tuşu, bağlantı) girdi ona uyar.
  useEffect(() => {
    setYerel(deger);
  }, [deger]);

  useEffect(() => {
    if (yerel === deger) return;
    const zamanlayici = setTimeout(() => degisti(yerel), 300);
    return () => clearTimeout(zamanlayici);
  }, [yerel, deger, degisti]);

  return (
    <div className="p-4 pb-0">
      <Field label="Ara" hint={ipucu}>
        {({ id, describedBy }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            // `type="search"` mobilde temizleme düğmesi ve doğru klavye verir.
            type="search"
            inputMode="search"
            autoCapitalize="none"
            autoCorrect="off"
            value={yerel}
            onChange={(e) => setYerel(e.target.value)}
          />
        )}
      </Field>
    </div>
  );
}

/** Adres ↔ arama kutusu köprüsü; her listede aynı olduğu için burada. */
export function useAramaYazici(yaz: (q: string | undefined) => void): (v: string) => void {
  return useCallback(
    (v: string) => {
      const temiz = v.trim();
      yaz(temiz === '' ? undefined : temiz);
    },
    [yaz],
  );
}

/* ── Liste durumu ────────────────────────────────────────────────────────── */

interface ListeDurumuProps<T> {
  readonly sorgu: UseQueryResult<readonly T[]>;
  readonly bosBaslik: string;
  readonly bosAciklama: string;
  /** Filtre uygulanmışken boş liste FARKLI bir durumdur — aşağıya bakın. */
  readonly filtreVar: boolean;
  readonly filtreyiTemizle: () => void;
  /** Gerçekten boş listede sunulan ÇIKIŞ — boş liste çıkmaz olmamalı. */
  readonly bosEylem?: ReactNode;
  readonly children: (satirlar: readonly T[]) => ReactNode;
}

/**
 * Dört hâl, dört davranış — `RequireSession`'daki ayrımın liste hâli.
 *
 *   YÜKLENİYOR → iskelet (ekran zıplamasın)
 *   HATA       → sebep + TEKRAR DENE. "Kayıt yok" DEME: hata, boşluk değildir.
 *                Bu ikisini karıştırmak en pahalı yanlıştır — kullanıcı
 *                sahalarının silindiğini sanır.
 *   FİLTRELİ BOŞ → "sonuç yok" + filtreyi temizleme yolu
 *   GERÇEKTEN BOŞ → "henüz kayıt yok"
 */
export function ListeDurumu<T>({
  sorgu,
  bosBaslik,
  bosAciklama,
  filtreVar,
  filtreyiTemizle,
  bosEylem,
  children,
}: ListeDurumuProps<T>): React.JSX.Element {
  if (sorgu.isPending) return <SkeletonList rows={4} />;

  if (sorgu.isError) {
    return (
      <EmptyState
        title="Liste yüklenemedi"
        description={sorgu.error instanceof Error ? sorgu.error.message : 'Bilinmeyen bir hata.'}
        action={<Button onClick={() => void sorgu.refetch()}>Tekrar dene</Button>}
      />
    );
  }

  const satirlar = sorgu.data;

  if (satirlar.length === 0) {
    return filtreVar ? (
      <EmptyState
        title="Eşleşen kayıt yok"
        description="Arama veya filtreyi değiştirip tekrar deneyin."
        action={
          <Button variant="secondary" onClick={filtreyiTemizle}>
            Filtreyi temizle
          </Button>
        }
      />
    ) : (
      <EmptyState
        title={bosBaslik}
        description={bosAciklama}
        {...(bosEylem === undefined ? {} : { action: bosEylem })}
      />
    );
  }

  return (
    <>
      {children(satirlar)}
      <TavanUyarisi sayi={satirlar.length} />
    </>
  );
}

/**
 * "İlk 200" uyarısı — SESSİZ KIRPMA YOK.
 *
 * Sunucu `LIMIT 200` uyguluyor. 200 satır dönmüşse listede olmayan kayıtlar
 * OLABİLİR; bunu söylemezsek kullanıcı aradığı sahayı yok sanar ve — daha
 * kötüsü — ikinci kez oluşturur.
 */
export function TavanUyarisi({ sayi }: { readonly sayi: number }): React.JSX.Element | null {
  if (sayi < LISTE_TAVANI) return null;
  return (
    <p role="status" className="mt-4 rounded-[var(--radius-card)] bg-warn-bg p-3 text-sm text-warn">
      Yalnız ilk {LISTE_TAVANI} kayıt gösteriliyor. Aramayı daraltın.
    </p>
  );
}

/* ── Ekleme yolu ─────────────────────────────────────────────────────────── */

/**
 * "Ekle" bağlantısı — izni olmayana GÖSTERİLMEZ.
 *
 * D adımında burada devre dışı bir düğme denenmiş ve silinmişti: var olmayan
 * bir yolu göstermek, hiç göstermemekten kötüdür. Artık yol var.
 *
 * Bağlantı, düğme değil: orta tuşla yeni sekmede açılabilsin ve adres
 * kopyalanabilsin.
 */
export function EklemeBaglantisi({
  gerekli,
  to,
  etiket,
  vurgulu = false,
}: {
  readonly gerekli: string;
  readonly to: '/sahalar/yeni' | '/panolar/yeni' | '/tanklar/yeni';
  readonly etiket: string;
  readonly vurgulu?: boolean;
}): React.JSX.Element | null {
  const { data: session } = useSession();
  if (!izinVar(session, gerekli)) return null;

  return (
    <Link
      to={to}
      className={cx(
        'inline-flex min-h-11 items-center justify-center rounded-[var(--radius-control)] px-4 text-sm font-medium',
        vurgulu
          ? 'bg-brand-600 text-white hover:bg-brand-700'
          : 'border border-border bg-surface text-ink hover:bg-surface-sunken',
      )}
    >
      {etiket}
    </Link>
  );
}

/* ── Filtre çubuğu ───────────────────────────────────────────────────────── */

/**
 * Açık/kapalı filtre düğmesi.
 *
 * `aria-pressed` ŞART: görsel olarak "basılı" duran bir düğmenin durumu ekran
 * okuyucuya başka türlü ulaşmaz ve kullanıcı filtrenin açık olduğunu bilmez —
 * boş liste gördüğünde kaydın silindiğini sanar.
 */
export function FiltreDugmesi({
  etkin,
  onClick,
  children,
}: {
  readonly etkin: boolean;
  readonly onClick: () => void;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={etkin}
      onClick={onClick}
      className={cx(
        'inline-flex min-h-9 items-center rounded-full border px-3 text-sm font-medium',
        etkin
          ? 'border-brand-600 bg-brand-50 text-brand-700'
          : 'border-border bg-surface text-ink-muted hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

/** Filtre satırı — dar ekranda yatay kayar, sıkışıp okunmaz hâle gelmez. */
export function FiltreSatiri({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <div className="flex gap-2 overflow-x-auto px-4 pt-3">{children}</div>;
}

/* ── Operatör kapsam notu ────────────────────────────────────────────────── */

/**
 * Firma seçmemiş operatöre "buradan yazamazsın" der.
 *
 * [K-010]: kapsamsız operatör TÜM firmaları okur ama hiçbirine yazamaz.
 * Uyarıyı listede vermezsek kullanıcı formu doldurur, kaydete basar ve
 * `409 no_active_firm` alır — emeği boşa gider. Hata mesajı yeterli değil;
 * doğru yer, işe BAŞLAMADAN önce.
 */
export function KapsamNotu(): React.JSX.Element | null {
  const { data: session } = useSession();
  if (!session?.isOperator) return null;
  if (session.activeFirmId !== null) return null;

  return (
    <p
      role="status"
      className="mx-4 mt-4 rounded-[var(--radius-card)] border border-border bg-surface p-3 text-sm text-ink-muted"
    >
      Tüm firmaları görüyorsunuz — <strong>salt okuma</strong>. Kayıt eklemek veya düzenlemek için
      profil sayfasından bir firma seçin.
    </p>
  );
}
